import React, { createContext, useContext, useEffect, useState } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "./supabaseClient";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Keys we leave behind on sign-out / user switch — e.g. anonymous landing
 *  preferences and per-user records that are already namespaced by user id
 *  and therefore can't leak between accounts. */
const PRESERVED_LOCAL_KEYS = new Set<string>([
  // i18n + theme + cookie banners etc. could go here in the future.
]);

const LAST_USER_ID_KEY = "oplif_last_user_id";
const LAST_TZ_SYNC_KEY = "oplif_last_tz_sync"; // value: `${userId}:${timezone}`

/** Detect the browser's IANA time zone and persist it on the user's profile
 *  so server-side jobs (e.g. weekly digest) can target each user's local
 *  time. No-ops if the browser doesn't expose a TZ or if we already synced
 *  this exact value for this user on this browser. */
function syncBrowserTimezone(u: User | null) {
  if (!u || typeof window === "undefined") return;
  let tz = "";
  try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch {}
  if (!tz) return;
  try {
    const last = window.localStorage.getItem(LAST_TZ_SYNC_KEY);
    if (last === `${u.id}:${tz}`) return;
  } catch {}
  supabase
    .from("profiles")
    .update({ timezone: tz })
    .eq("user_id", u.id)
    .then(({ error }) => {
      if (!error) {
        try { window.localStorage.setItem(LAST_TZ_SYNC_KEY, `${u.id}:${tz}`); } catch {}
      }
    }, () => {});
}

/** Wipe every per-user "oplif_*" localStorage entry so the next account that
 *  signs in on this browser starts from a clean state. Per-user-namespaced
 *  keys (e.g. `oplif_active_location_<uid>`) are also removed for safety. */
function clearOplifLocalState() {
  if (typeof window === "undefined") return;
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (!k) continue;
      if (!k.startsWith("oplif_")) continue;
      if (k === LAST_USER_ID_KEY) continue;
      if (PRESERVED_LOCAL_KEYS.has(k)) continue;
      toRemove.push(k);
    }
    toRemove.forEach((k) => window.localStorage.removeItem(k));
  } catch {}
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const k = window.sessionStorage.key(i);
      if (k && k.startsWith("oplif_")) toRemove.push(k);
    }
    toRemove.forEach((k) => window.sessionStorage.removeItem(k));
  } catch {}
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const notifyAdminIfNewSignup = (u: User | null) => {
      if (!u) return;
      const createdAt = u.created_at ? new Date(u.created_at).getTime() : 0;
      const lastSignIn = u.last_sign_in_at ? new Date(u.last_sign_in_at).getTime() : 0;
      // First-ever sign-in: created and last_sign_in within 60s of each other.
      const isFirstSignIn = createdAt > 0 && Math.abs(lastSignIn - createdAt) < 60_000;
      if (!isFirstSignIn) return;
      const key = `signup-${u.id}`;
      // Client-side dedupe across page reloads.
      if (typeof window !== "undefined" && window.localStorage.getItem(key)) return;
      try { window.localStorage.setItem(key, "1"); } catch {}
      // 1. Enqueue the 4-email onboarding sequence (welcome now, day1/3/7 later).
      //    The edge function authenticates via JWT, inserts schedule rows
      //    with service-role privileges, and triggers immediate dispatch.
      if (u.email) {
        supabase.functions
          .invoke("enqueue-onboarding-sequence", { body: {} })
          .catch(() => {});
      }
      // 2. Admin notification copy.
      supabase.functions.invoke("send-transactional-email", {
        body: {
          templateName: "admin-signup-notification",
          recipientEmail: "enrico@oplif.com",
          idempotencyKey: `admin-signup-${u.id}`,
          templateData: {
            userEmail: u.email,
            plan: "free",
            eventType: "signup",
            timestamp: new Date().toISOString(),
          },
        },
      }).catch(() => {});
    };

    /** When a *different* user signs in on this browser (or this is a first
     *  sign-in with stale demo/test data left behind), wipe the cached
     *  onboarding/results/etc. so the new account never sees another user's
     *  business name, revenue, or roadmap. */
    const handleUserSwitch = (u: User | null) => {
      if (typeof window === "undefined" || !u) return;
      try {
        const previous = window.localStorage.getItem(LAST_USER_ID_KEY);
        const createdAt = u.created_at ? new Date(u.created_at).getTime() : 0;
        const lastSignIn = u.last_sign_in_at ? new Date(u.last_sign_in_at).getTime() : 0;
        const isFirstSignIn =
          createdAt > 0 && Math.abs(lastSignIn - createdAt) < 60_000;
        if (previous !== u.id || isFirstSignIn) {
          clearOplifLocalState();
          window.localStorage.setItem(LAST_USER_ID_KEY, u.id);
        }
      } catch {}
    };

    // CRITICAL: register the listener BEFORE calling getSession() so we never
    // miss an INITIAL_SESSION / TOKEN_REFRESHED event fired during boot.
    // Only flip `loading` to false AFTER getSession() resolves — otherwise a
    // momentary null from an early callback can cause ProtectedRoute to
    // bounce the user to /auth (e.g. when returning from a Stripe redirect).
    let initialised = false;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (initialised) {
        // Subsequent auth changes (sign in/out/refresh) — safe to clear loading.
        setLoading(false);
      }
      if (event === "SIGNED_IN") {
        handleUserSwitch(session?.user ?? null);
        // Defer to avoid blocking the auth state callback.
        setTimeout(() => notifyAdminIfNewSignup(session?.user ?? null), 0);
        setTimeout(() => syncBrowserTimezone(session?.user ?? null), 0);
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      // Same guard for the initial boot — handles the case where INITIAL_SESSION
      // fires before the listener is wired in some browsers.
      handleUserSwitch(session?.user ?? null);
      // Persist browser timezone so server-side weekly emails can target each
      // user's local time. Cheap to call — debounced via localStorage.
      syncBrowserTimezone(session?.user ?? null);
      initialised = true;
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    // Clear cached onboarding/results so the next account that signs in on
    // this browser starts completely clean.
    clearOplifLocalState();
    try {
      if (typeof window !== "undefined") {
        window.localStorage.removeItem(LAST_USER_ID_KEY);
      }
    } catch {}
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
